@file:NoLiveLiterals

package com.example.borshi.web.nutritionist.ui

import androidx.compose.runtime.*
import androidx.compose.runtime.NoLiveLiterals
import com.example.borshi.web.nutritionist.repository.Conversation
import com.example.borshi.web.nutritionist.repository.Message
import com.example.borshi.web.nutritionist.repository.NutritionistRepository
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

// ─── Design tokens ───────────────────────────────────────────────────────────
private const val M_INK        = "#0D2B2E"
private const val M_INK_LIGHT  = "#7A9598"
private const val M_TEAL       = "#26A69A"
private const val M_TEAL_DEEP  = "#1A7A72"
private const val M_TEAL_GHOST = "#E0F2F1"
private const val M_SURFACE    = "#F5F9F8"
private const val M_WHITE      = "#FFFFFF"
private const val M_BORDER     = "#E0EDEB"
private const val M_SHADOW_SM  = "0 1px 4px rgba(13,43,46,0.06)"
private const val M_DANGER_BG  = "#FCE4EC"
private const val M_DANGER     = "#C62828"

@Composable
fun MessagingView(repository: NutritionistRepository, nutritionistId: String) {
    var conversations     by remember { mutableStateOf(emptyList<Conversation>()) }
    var selectedConvId    by remember { mutableStateOf<String?>(null) }
    var messages          by remember { mutableStateOf(emptyList<Message>()) }
    var messageText       by remember { mutableStateOf("") }
    var searchQuery       by remember { mutableStateOf("") }
    var isLoadingConvs    by remember { mutableStateOf(true) }
    var isLoadingMessages by remember { mutableStateOf(false) }
    var isSending         by remember { mutableStateOf(false) }
    var sendError         by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()

    val selectedConv: Conversation? = conversations.firstOrNull { it.conversationId == selectedConvId }

    // ── Load conversations ────────────────────────────────────────────────
    LaunchedEffect(nutritionistId) {
        if (nutritionistId.isBlank()) { isLoadingConvs = false; return@LaunchedEffect }
        isLoadingConvs = true
        repository.getConversations(nutritionistId)
            .onSuccess { conversations = it }
            .onFailure { console.warn("MessagingView: conversations load failed:", it.message) }
        isLoadingConvs = false
    }

    // ── Load messages when conversation changes ───────────────────────────
    LaunchedEffect(selectedConvId) {
        val cid = selectedConvId ?: return@LaunchedEffect
        isLoadingMessages = true
        repository.getMessages(cid, limit = 100)
            .onSuccess  { messages = it }
            .onFailure  { console.warn("MessagingView: messages load failed:", it.message) }
        isLoadingMessages = false
    }

    // ── Inject CSS ────────────────────────────────────────────────────────
    DisposableEffect(Unit) {
        if (kotlinx.browser.document.getElementById("msg-styles") == null) {
            val s = kotlinx.browser.document.createElement("style")
            s.id = "msg-styles"
            s.textContent = """
.msg-root{display:grid;grid-template-columns:300px 1fr;height:calc(100vh - 200px);
    background:$M_WHITE;border-radius:20px;border:1.5px solid $M_BORDER;
    box-shadow:$M_SHADOW_SM;overflow:hidden}
.msg-sidebar{border-right:1.5px solid $M_BORDER;display:flex;flex-direction:column;background:$M_SURFACE}
.msg-sb-header{padding:20px;border-bottom:1.5px solid $M_BORDER}
.msg-sb-title{font-family:'Syne',sans-serif;font-size:16px;font-weight:800;color:$M_INK;margin-bottom:12px}
.msg-search{width:100%;padding:9px 12px;border:1.5px solid $M_BORDER;border-radius:10px;
    font-size:13px;font-family:'DM Sans',sans-serif;color:$M_INK;background:$M_WHITE;
    outline:none;transition:border-color 0.15s;box-sizing:border-box}
.msg-search:focus{border-color:$M_TEAL}
.msg-convs{flex:1;overflow-y:auto}
.msg-conv-item{padding:14px 16px;border-bottom:1px solid $M_BORDER;cursor:pointer;
    display:flex;align-items:center;gap:12px;transition:background 0.15s}
.msg-conv-item:hover{background:$M_TEAL_GHOST}
.msg-conv-item.active{background:$M_TEAL_GHOST;border-left:3px solid $M_TEAL}
.msg-avatar{width:40px;height:40px;border-radius:50%;
    background:linear-gradient(135deg,$M_INK,$M_TEAL);
    display:flex;align-items:center;justify-content:center;
    color:white;font-size:15px;font-weight:800;font-family:'Syne',sans-serif;flex-shrink:0}
.msg-conv-name{font-size:14px;font-weight:700;color:$M_INK}
.msg-conv-last{font-size:12px;color:$M_INK_LIGHT;margin-top:2px;
    white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:160px}
.msg-unread{min-width:20px;height:20px;border-radius:99px;background:$M_TEAL;
    color:white;font-size:11px;font-weight:700;display:flex;align-items:center;
    justify-content:center;padding:0 5px;margin-left:auto}
.msg-main{display:flex;flex-direction:column}
.msg-main-header{padding:16px 20px;border-bottom:1.5px solid $M_BORDER;
    display:flex;align-items:center;gap:12px;background:$M_WHITE}
.msg-main-name{font-family:'Syne',sans-serif;font-size:16px;font-weight:800;color:$M_INK}
.msg-area{flex:1;overflow-y:auto;padding:20px;background:$M_SURFACE;
    display:flex;flex-direction:column;gap:12px}
.msg-row-sent{display:flex;justify-content:flex-end}
.msg-row-recv{display:flex;justify-content:flex-start}
.msg-bubble-sent{max-width:70%;padding:10px 14px;border-radius:16px 16px 4px 16px;
    background:$M_TEAL;color:white;font-size:14px;line-height:1.5;font-family:'DM Sans',sans-serif}
.msg-bubble-recv{max-width:70%;padding:10px 14px;border-radius:16px 16px 16px 4px;
    background:$M_WHITE;color:$M_INK;border:1.5px solid $M_BORDER;
    font-size:14px;line-height:1.5;font-family:'DM Sans',sans-serif}
.msg-bubble-time{font-size:10px;margin-top:4px;opacity:0.7;text-align:right}
.msg-input-row{padding:12px 16px;border-top:1.5px solid $M_BORDER;
    display:flex;gap:10px;align-items:center;background:$M_WHITE}
.msg-input{flex:1;padding:10px 14px;border:1.5px solid $M_BORDER;border-radius:99px;
    font-size:14px;font-family:'DM Sans',sans-serif;color:$M_INK;
    outline:none;transition:border-color 0.15s}
.msg-input:focus{border-color:$M_TEAL}
.msg-send{padding:10px 20px;background:$M_TEAL;color:white;border:none;
    border-radius:99px;font-size:13px;font-weight:700;cursor:pointer;
    font-family:'DM Sans',sans-serif;transition:background 0.15s;white-space:nowrap}
.msg-send:hover{background:$M_TEAL_DEEP}
.msg-send:disabled{background:$M_BORDER;cursor:not-allowed;color:$M_INK_LIGHT}
.msg-empty{display:flex;flex-direction:column;align-items:center;
    justify-content:center;height:100%;color:$M_INK_LIGHT;text-align:center;padding:40px}
.msg-muted{padding:20px;text-align:center;color:$M_INK_LIGHT;font-size:14px}
.msg-err{padding:8px 16px;background:$M_DANGER_BG;color:$M_DANGER;font-size:13px}
@media(max-width:768px){.msg-root{grid-template-columns:1fr}.msg-sidebar{display:none}}
            """.trimIndent()
            kotlinx.browser.document.head?.appendChild(s)
        }
        onDispose {}
    }

    // ── Derived state (computed outside Composable tree) ─────────────────
    val filteredConvs: List<Conversation> = if (searchQuery.isBlank()) conversations
    else conversations.filter { c ->
        c.participantNames.values.any { n -> n.contains(searchQuery, ignoreCase = true) }
    }

    Div({ classes("msg-root") }) {

        // ── LEFT: conversation list ───────────────────────────────────────
        Div({ classes("msg-sidebar") }) {
            Div({ classes("msg-sb-header") }) {
                Div({ classes("msg-sb-title") }) { Text("💬 Messages") }
                Input(org.jetbrains.compose.web.attributes.InputType.Text) {
                    classes("msg-search")
                    value(searchQuery)
                    onInput { searchQuery = it.value }
                    attr("placeholder", "Search conversations…")
                }
            }
            Div({ classes("msg-convs") }) {
                when {
                    isLoadingConvs -> Div({ classes("msg-muted") }) { Text("Loading…") }
                    filteredConvs.isEmpty() -> Div({ classes("msg-muted") }) {
                        Text(if (conversations.isEmpty()) "No conversations yet" else "No results")
                    }
                    else -> {
                        filteredConvs.forEach { conv ->
                            val isActive = conv.conversationId == selectedConvId
                            val otherName = conv.participantNames.entries
                                .firstOrNull { e -> e.key != nutritionistId }?.value ?: "Patient"
                            val unread = conv.unreadCount[nutritionistId] ?: 0
                            Div({
                                classes("msg-conv-item", if (isActive) "active" else "")
                                onClick {
                                    selectedConvId = conv.conversationId
                                    if (unread > 0) {
                                        scope.launch {
                                            repository.markConversationAsRead(conv.conversationId, nutritionistId)
                                        }
                                    }
                                }
                            }) {
                                Div({ classes("msg-avatar") }) { Text(otherName.take(1).uppercase()) }
                                Div({ style { flex(1); minWidth(0.px) } }) {
                                    Div({ classes("msg-conv-name") }) { Text(otherName) }
                                    Div({ classes("msg-conv-last") }) {
                                        Text(if (conv.lastMessage.isBlank()) "Start a conversation" else conv.lastMessage)
                                    }
                                }
                                if (unread > 0) {
                                    Div({ classes("msg-unread") }) { Text(unread.toString()) }
                                }
                            }
                        }
                    }
                }
            }
        }

        // ── RIGHT: message thread ─────────────────────────────────────────
        if (selectedConv == null) {
            Div({ classes("msg-empty") }) {
                Div({ style { fontSize(48.px); marginBottom(16.px) } }) { Text("💬") }
                Div({ style { fontSize(18.px); fontWeight("700"); color(Color(M_INK)); marginBottom(8.px) } }) {
                    Text("Select a conversation")
                }
                Div({ style { fontSize(14.px) } }) {
                    Text("Pick a patient from the left to start messaging")
                }
            }
        } else {
            val otherName = selectedConv.participantNames.entries
                .firstOrNull { e -> e.key != nutritionistId }?.value ?: "Patient"

            Div({ classes("msg-main") }) {
                // Header
                Div({ classes("msg-main-header") }) {
                    Div({ classes("msg-avatar") }) { Text(otherName.take(1).uppercase()) }
                    Div({ classes("msg-main-name") }) { Text(otherName) }
                }

                // Messages area
                Div({ classes("msg-area") }) {
                    when {
                        isLoadingMessages -> Div({ classes("msg-muted") }) { Text("Loading messages…") }
                        messages.isEmpty() -> Div({ classes("msg-muted"); style { marginTop(40.px) } }) {
                            Text("No messages yet — say hello! 👋")
                        }
                        else -> {
                            messages.forEach { msg ->
                                val isSent = msg.senderId == nutritionistId
                                Div({ classes(if (isSent) "msg-row-sent" else "msg-row-recv") }) {
                                    Div({ classes(if (isSent) "msg-bubble-sent" else "msg-bubble-recv") }) {
                                        Div { Text(msg.content) }
                                        Div({ classes("msg-bubble-time") }) {
                                            Text(msgFormatTime(msg.timestamp))
                                        }
                                    }
                                }
                            }
                        }
                    }
                }

                // Error
                if (sendError != null) {
                    Div({ classes("msg-err") }) { Text("⚠️ $sendError") }
                }

                // Input row
                Div({ classes("msg-input-row") }) {
                    Input(org.jetbrains.compose.web.attributes.InputType.Text) {
                        classes("msg-input")
                        value(messageText)
                        onInput { messageText = it.value; sendError = null }
                        attr("placeholder", "Type a message…")
                        onKeyDown { e ->
                            if (e.key == "Enter" && !e.shiftKey) {
                                val text = messageText.trim()
                                if (text.isNotBlank() && !isSending) {
                                    val cid = selectedConv.conversationId
                                    scope.launch {
                                        isSending = true; sendError = null
                                        val outgoing = Message(
                                            messageId      = "tmp_${kotlin.js.Date.now().toLong()}",
                                            conversationId = cid,
                                            senderId       = nutritionistId,
                                            senderName     = "Nutritionist",
                                            content        = text,
                                            timestamp      = kotlin.js.Date.now().toLong(),
                                            readBy         = listOf(nutritionistId),
                                            messageType    = "text"
                                        )
                                        messages = messages + outgoing
                                        messageText = ""
                                        repository.sendMessage(outgoing)
                                            .onSuccess { newId ->
                                                messages = messages.map { m ->
                                                    if (m.messageId == outgoing.messageId) m.copy(messageId = newId) else m
                                                }
                                                repository.getConversations(nutritionistId)
                                                    .onSuccess { conversations = it }
                                            }
                                            .onFailure {
                                                messages = messages.filterNot { m -> m.messageId == outgoing.messageId }
                                                messageText = text
                                                sendError = "Failed to send: ${it.message}"
                                            }
                                        isSending = false
                                    }
                                }
                            }
                        }
                    }
                    Button({
                        classes("msg-send")
                        if (messageText.isBlank() || isSending) attr("disabled", "disabled")
                        onClick {
                            val text = messageText.trim()
                            if (text.isNotBlank() && !isSending) {
                                val cid = selectedConv.conversationId
                                scope.launch {
                                    isSending = true; sendError = null
                                    val outgoing = Message(
                                        messageId      = "tmp_${kotlin.js.Date.now().toLong()}",
                                        conversationId = cid,
                                        senderId       = nutritionistId,
                                        senderName     = "Nutritionist",
                                        content        = text,
                                        timestamp      = kotlin.js.Date.now().toLong(),
                                        readBy         = listOf(nutritionistId),
                                        messageType    = "text"
                                    )
                                    messages = messages + outgoing
                                    messageText = ""
                                    repository.sendMessage(outgoing)
                                        .onSuccess { newId ->
                                            messages = messages.map { m ->
                                                if (m.messageId == outgoing.messageId) m.copy(messageId = newId) else m
                                            }
                                            repository.getConversations(nutritionistId)
                                                .onSuccess { conversations = it }
                                        }
                                        .onFailure {
                                            messages = messages.filterNot { m -> m.messageId == outgoing.messageId }
                                            messageText = text
                                            sendError = "Failed to send: ${it.message}"
                                        }
                                    isSending = false
                                }
                            }
                        }
                    }) { Text(if (isSending) "Sending…" else "Send ➤") }
                }
            }
        }
    }
}

private fun msgFormatTime(timestamp: Long): String {
    val date = kotlin.js.Date(timestamp.toDouble())
    return "${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}"
}
